/**
 * Publieke embeddable Meeting Timer pagina.
 * Wizard: contact → vragen → timer (met tips + luid eindgeluid).
 * Bij submit van contact wordt automatisch een lead in het CRM gemaakt
 * met source 'meeting_timer'. Beheer (vragen, tips, instellingen) gebeurt
 * in de "Tool Hub" tab in het CRM.
 */
import { useState, useEffect, useMemo, useRef } from 'react';
import { meetingTimerAPI } from '@/lib/api';
import {
  Clock, Sparkles, Mail, User, Building2, Lightbulb, Loader2,
  Play, Pause, RotateCcw, ChevronRight, ChevronLeft, Check, AlertCircle,
  Volume2, Timer as TimerIcon, X
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent } from '@/components/ui/card';
import { playEndSound, unlockAudio } from '@/lib/timerAudio';
import BookingCta from '@/components/BookingCta';

const LOGO_ICON = 'https://customer-assets.emergentagent.com/job_buyer-match-26/artifacts/52px3160_zynq_logo_icon_e0dde9b0559f42738516da0422c8a0d0.png';

function pad(n) {
  return String(Math.max(0, Math.floor(n))).padStart(2, '0');
}

function formatTime(totalSeconds) {
  const s = Math.max(0, Math.floor(totalSeconds));
  const m = Math.floor(s / 60);
  const sec = s % 60;
  return `${pad(m)}:${pad(sec)}`;
}

export default function MeetingTimerPage() {
  const [config, setConfig] = useState(null);
  const [loadingConfig, setLoadingConfig] = useState(true);
  const [loadError, setLoadError] = useState('');

  // Wizard state
  const [step, setStep] = useState(1); // 1=contact, 2=questions, 3=timer, 4=done
  const [contact, setContact] = useState({ name: '', email: '', company: '' });
  const [meeting, setMeeting] = useState({});
  const [stepError, setStepError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Timer state
  const [durationMin, setDurationMin] = useState(30);
  const [remaining, setRemaining] = useState(30 * 60);
  const [running, setRunning] = useState(false);
  const [ended, setEnded] = useState(false);
  const intervalRef = useRef(null);
  const endAtRef = useRef(null);

  // Tip rotation
  const [tipIndex, setTipIndex] = useState(0);

  // Force light mode op publieke pagina
  useEffect(() => {
    const root = document.documentElement;
    const hadDark = root.classList.contains('dark');
    root.classList.remove('dark');
    return () => {
      if (hadDark) root.classList.add('dark');
    };
  }, []);

  // Load config
  useEffect(() => {
    meetingTimerAPI.getConfig()
      .then(res => {
        setConfig(res.data);
        // Set default duration from config
        const dur = Number(res.data?.default_duration_min) || 30;
        setDurationMin(dur);
        setRemaining(dur * 60);
        // Init defaults for questions
        const initial = {};
        (res.data?.questions || []).forEach(q => {
          if (q.default !== undefined && q.default !== null) {
            initial[q.key] = q.default;
          } else if (q.type === 'multi_choice') {
            initial[q.key] = [];
          }
        });
        setMeeting(initial);
      })
      .catch(() => setLoadError('Kon de tool niet laden. Probeer het later opnieuw.'))
      .finally(() => setLoadingConfig(false));
  }, []);

  // Tip rotation
  useEffect(() => {
    if (step !== 3) return;
    const tips = config?.tips || [];
    if (tips.length <= 1) return;
    const interval = setInterval(() => {
      setTipIndex(i => (i + 1) % tips.length);
    }, 9000);
    return () => clearInterval(interval);
  }, [step, config]);

  // Timer interval — drift-vrij via een vaste eindtijd
  useEffect(() => {
    if (!running) return;
    intervalRef.current = setInterval(() => {
      if (!endAtRef.current) return;
      const left = Math.round((endAtRef.current - Date.now()) / 1000);
      if (left <= 0) {
        setRemaining(0);
        setRunning(false);
        setEnded(true);
        // Speel eindgeluid
        playEndSound(config?.sound_key || 'bell');
      } else {
        setRemaining(left);
      }
    }, 250);
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [running, config]);

  const handleStartTimer = () => {
    unlockAudio();
    if (remaining <= 0) {
      // Reset eerst
      setRemaining(durationMin * 60);
      setEnded(false);
      endAtRef.current = Date.now() + durationMin * 60 * 1000;
    } else {
      endAtRef.current = Date.now() + remaining * 1000;
    }
    setRunning(true);
  };

  const handlePauseTimer = () => {
    setRunning(false);
  };

  const handleResetTimer = () => {
    setRunning(false);
    setEnded(false);
    setRemaining(durationMin * 60);
    endAtRef.current = null;
  };

  const handleDurationChange = (newMin) => {
    const m = Math.max(1, Math.min(480, Number(newMin) || 1));
    setDurationMin(m);
    if (!running) {
      setRemaining(m * 60);
      setEnded(false);
    }
  };

  // ── Validatie ──
  const validateContact = () => {
    if (!contact.name.trim()) return 'Vul je naam in om verder te gaan.';
    if (!contact.email.trim()) return 'Vul je e-mailadres in om verder te gaan.';
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(contact.email.trim())) return 'Vul een geldig e-mailadres in.';
    return '';
  };

  const validateMeeting = () => {
    for (const q of config?.questions || []) {
      if (!q.required) continue;
      const v = meeting[q.key];
      const empty = v === undefined || v === null || v === '' || (Array.isArray(v) && v.length === 0);
      if (empty) return `Vul "${q.label}" in om verder te gaan.`;
    }
    return '';
  };

  const handleNextFromContact = () => {
    const err = validateContact();
    if (err) { setStepError(err); return; }
    setStepError('');
    setStep(2);
  };

  const handleNextFromMeeting = async () => {
    const err = validateMeeting();
    if (err) { setStepError(err); return; }
    setStepError('');
    setSubmitting(true);
    try {
      // Bepaal duur uit antwoorden (als 'duration_min' vraag bestaat) of fallback default
      let proposedDuration = Number(meeting.duration_min) || config?.default_duration_min || 30;
      proposedDuration = Math.max(1, Math.min(480, proposedDuration));

      await meetingTimerAPI.start({
        contact,
        meeting,
        duration_min: proposedDuration,
      });

      // Zet timer-state op gekozen duur
      setDurationMin(proposedDuration);
      setRemaining(proposedDuration * 60);
      setEnded(false);
      setStep(3);
    } catch (e) {
      setStepError(e?.response?.data?.detail || 'Versturen mislukt. Probeer het opnieuw.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleBack = () => {
    setStepError('');
    setStep(s => Math.max(1, s - 1));
  };

  // ── Renderers ──
  if (loadingConfig) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-orange-50 via-white to-amber-50/40">
        <Loader2 className="w-8 h-8 text-orange-600 animate-spin" />
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-orange-50 via-white to-amber-50/40 p-6 text-slate-800">
        <div className="text-center max-w-md">
          <AlertCircle className="w-10 h-10 text-red-500 mx-auto mb-3" />
          <p className="text-slate-700">{loadError}</p>
        </div>
      </div>
    );
  }

  return (
    <div
      className="min-h-screen bg-gradient-to-br from-orange-50 via-white to-amber-50/40 py-8 px-4 text-slate-800"
      data-testid="meeting-timer-page"
    >
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <img src={LOGO_ICON} alt="Zynq" className="w-10 h-10 rounded-lg" />
            <div>
              <div className="font-bold text-lg leading-none text-slate-900">Zynq</div>
              <div className="text-xs text-slate-500">Meeting Timer</div>
            </div>
          </div>
          {step > 1 && step < 3 && (
            <button
              onClick={handleBack}
              className="text-xs text-slate-500 hover:text-orange-700 underline"
            >
              Stap terug
            </button>
          )}
        </div>

        {/* Progress */}
        {step < 3 && <ProgressBar step={step} />}

        {step === 1 && (
          <ContactStep
            config={config}
            contact={contact}
            setContact={setContact}
            error={stepError}
            onNext={handleNextFromContact}
          />
        )}

        {step === 2 && (
          <QuestionsStep
            config={config}
            meeting={meeting}
            setMeeting={setMeeting}
            error={stepError}
            submitting={submitting}
            onBack={handleBack}
            onNext={handleNextFromMeeting}
          />
        )}

        {step === 3 && (
          <TimerStep
            config={config}
            durationMin={durationMin}
            onDurationChange={handleDurationChange}
            remaining={remaining}
            running={running}
            ended={ended}
            onStart={handleStartTimer}
            onPause={handlePauseTimer}
            onReset={handleResetTimer}
            tipIndex={tipIndex}
            onCycleTip={() => {
              const tips = config?.tips || [];
              if (tips.length) setTipIndex(i => (i + 1) % tips.length);
            }}
            meeting={meeting}
          />
        )}

        {/* Footer */}
        <div className="mt-10 text-center text-xs text-slate-500">
          <p>© Zynq · Maak van elke meeting een succes</p>
        </div>
      </div>
    </div>
  );
}

// ─── Subcomponents ───
function ProgressBar({ step }) {
  const labels = ['Welkom', 'Meeting', 'Timer'];
  return (
    <div className="mb-8">
      <div className="flex items-center justify-between max-w-md mx-auto">
        {labels.map((lbl, i) => {
          const idx = i + 1;
          const done = step > idx;
          const active = step === idx;
          return (
            <div key={lbl} className="flex items-center flex-1">
              <div className="flex flex-col items-center">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold transition-all ${
                  done ? 'bg-orange-600 text-white' :
                  active ? 'bg-orange-600 text-white ring-4 ring-orange-100' :
                  'bg-gray-200 text-slate-500'
                }`}>
                  {done ? <Check className="w-4 h-4" /> : idx}
                </div>
                <span className={`text-[10px] mt-1 font-medium ${active || done ? 'text-orange-700' : 'text-slate-400'}`}>{lbl}</span>
              </div>
              {i < labels.length - 1 && (
                <div className={`flex-1 h-0.5 mx-2 mb-4 transition-all ${done ? 'bg-orange-600' : 'bg-gray-200'}`} />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function ContactStep({ config, contact, setContact, error, onNext }) {
  return (
    <div data-testid="step-contact">
      <div className="text-center mb-6">
        <div className="inline-flex w-14 h-14 rounded-2xl bg-orange-600 items-center justify-center mb-4 shadow-lg shadow-orange-600/20">
          <TimerIcon className="w-7 h-7 text-white" />
        </div>
        <h1 className="text-3xl md:text-4xl font-bold tracking-tight mb-2 text-slate-900">
          {config?.intro_title || 'Maak van je meeting een succes'}
        </h1>
        <p className="text-slate-600 max-w-xl mx-auto">
          {config?.intro_text || 'Beantwoord een paar korte vragen, zet de timer en blijf gefocust.'}
        </p>
      </div>

      <Card className="border-orange-100 shadow-sm">
        <CardContent className="p-6 space-y-4">
          <p className="text-sm text-slate-500">Vul kort je gegevens in om de tool te starten.</p>

          <div className="space-y-2">
            <Label htmlFor="contact-name" className="text-slate-700 font-medium flex items-center gap-2">
              <User className="w-4 h-4 text-orange-600" /> Naam <span className="text-red-500">*</span>
            </Label>
            <Input
              id="contact-name"
              data-testid="contact-name-input"
              placeholder="Jouw naam"
              value={contact.name}
              onChange={e => setContact(c => ({ ...c, name: e.target.value }))}
              className="bg-white border-slate-200 focus-visible:ring-orange-500"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="contact-email" className="text-slate-700 font-medium flex items-center gap-2">
              <Mail className="w-4 h-4 text-orange-600" /> E-mail <span className="text-red-500">*</span>
            </Label>
            <Input
              id="contact-email"
              data-testid="contact-email-input"
              type="email"
              placeholder="jij@bedrijf.nl"
              value={contact.email}
              onChange={e => setContact(c => ({ ...c, email: e.target.value }))}
              className="bg-white border-slate-200 focus-visible:ring-orange-500"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="contact-company" className="text-slate-700 font-medium flex items-center gap-2">
              <Building2 className="w-4 h-4 text-orange-600" /> Bedrijf <span className="text-slate-400 text-xs font-normal">(optioneel)</span>
            </Label>
            <Input
              id="contact-company"
              data-testid="contact-company-input"
              placeholder="Bedrijfsnaam"
              value={contact.company}
              onChange={e => setContact(c => ({ ...c, company: e.target.value }))}
              className="bg-white border-slate-200 focus-visible:ring-orange-500"
            />
          </div>

          {error && (
            <div className="bg-red-50 text-red-700 text-sm p-3 rounded-md border border-red-100 flex items-start gap-2">
              <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <span>{error}</span>
            </div>
          )}

          <Button
            onClick={onNext}
            data-testid="contact-next-btn"
            className="w-full bg-orange-600 hover:bg-orange-700 text-white"
          >
            Verder <ChevronRight className="w-4 h-4 ml-1" />
          </Button>
          <p className="text-[11px] text-slate-400 text-center">
            We slaan je gegevens veilig op om contact met je op te kunnen nemen.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}

function QuestionsStep({ config, meeting, setMeeting, error, submitting, onBack, onNext }) {
  const setVal = (k, v) => setMeeting(m => ({ ...m, [k]: v }));
  const toggleMulti = (k, v) => {
    setMeeting(m => {
      const arr = Array.isArray(m[k]) ? m[k] : [];
      return { ...m, [k]: arr.includes(v) ? arr.filter(x => x !== v) : [...arr, v] };
    });
  };

  return (
    <div data-testid="step-questions">
      <div className="text-center mb-6">
        <h2 className="text-2xl md:text-3xl font-bold tracking-tight mb-2 text-slate-900">
          Vertel ons over je <span className="text-orange-600">meeting</span>
        </h2>
        <p className="text-slate-600">Een paar korte vragen zodat je gericht aan de slag kunt.</p>
      </div>

      <Card className="border-orange-100 shadow-sm">
        <CardContent className="p-6 space-y-5">
          {(config?.questions || []).map(q => (
            <QuestionField
              key={q.key}
              q={q}
              value={meeting[q.key]}
              onChange={(v) => setVal(q.key, v)}
              onToggleMulti={(v) => toggleMulti(q.key, v)}
            />
          ))}

          {error && (
            <div className="bg-red-50 text-red-700 text-sm p-3 rounded-md border border-red-100 flex items-start gap-2">
              <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <span>{error}</span>
            </div>
          )}

          <div className="flex gap-2 pt-2">
            <Button
              type="button"
              variant="outline"
              onClick={onBack}
              className="flex-1 border-slate-200 text-slate-700"
            >
              <ChevronLeft className="w-4 h-4 mr-1" /> Terug
            </Button>
            <Button
              onClick={onNext}
              data-testid="questions-next-btn"
              disabled={submitting}
              className="flex-1 bg-orange-600 hover:bg-orange-700 text-white"
            >
              {submitting ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Bezig…</>
              ) : (
                <>Start timer <ChevronRight className="w-4 h-4 ml-1" /></>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function QuestionField({ q, value, onChange, onToggleMulti }) {
  const baseInput = "bg-white border-slate-200 focus-visible:ring-orange-500";

  if (q.type === 'textarea') {
    return (
      <div className="space-y-2">
        <Label className="text-slate-700 font-medium">
          {q.label}{q.required && <span className="text-red-500"> *</span>}
        </Label>
        {q.description && <p className="text-xs text-slate-500">{q.description}</p>}
        <Textarea
          data-testid={`q-${q.key}`}
          placeholder={q.placeholder || ''}
          value={value || ''}
          onChange={e => onChange(e.target.value)}
          className={`${baseInput} min-h-[100px]`}
        />
      </div>
    );
  }

  if (q.type === 'number') {
    return (
      <div className="space-y-2">
        <Label className="text-slate-700 font-medium">
          {q.label}{q.required && <span className="text-red-500"> *</span>}
        </Label>
        {q.description && <p className="text-xs text-slate-500">{q.description}</p>}
        <Input
          data-testid={`q-${q.key}`}
          type="number"
          min="0"
          placeholder={q.placeholder || ''}
          value={value ?? ''}
          onChange={e => onChange(e.target.value === '' ? '' : Number(e.target.value))}
          className={baseInput}
        />
      </div>
    );
  }

  if (q.type === 'duration') {
    const presets = [15, 25, 30, 45, 60, 90];
    return (
      <div className="space-y-2">
        <Label className="text-slate-700 font-medium">
          {q.label}{q.required && <span className="text-red-500"> *</span>}
        </Label>
        {q.description && <p className="text-xs text-slate-500">{q.description}</p>}
        <div className="flex flex-wrap gap-2">
          {presets.map(p => (
            <button
              key={p}
              type="button"
              data-testid={`q-${q.key}-preset-${p}`}
              onClick={() => onChange(p)}
              className={`px-3 py-1.5 rounded-full text-sm font-medium border transition-all ${
                Number(value) === p
                  ? 'bg-orange-600 text-white border-orange-600'
                  : 'bg-white border-slate-200 text-slate-700 hover:border-orange-300'
              }`}
            >
              {p} min
            </button>
          ))}
          <Input
            type="number"
            min="1"
            max="480"
            data-testid={`q-${q.key}-custom`}
            placeholder="Anders…"
            value={value ?? ''}
            onChange={e => onChange(e.target.value === '' ? '' : Number(e.target.value))}
            className={`${baseInput} w-28`}
          />
        </div>
      </div>
    );
  }

  if (q.type === 'single_choice') {
    return (
      <div className="space-y-2">
        <Label className="text-slate-700 font-medium">
          {q.label}{q.required && <span className="text-red-500"> *</span>}
        </Label>
        {q.description && <p className="text-xs text-slate-500">{q.description}</p>}
        <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
          {(q.options || []).map(opt => {
            const v = opt.value ?? opt.label;
            const active = value === v;
            return (
              <button
                key={v}
                type="button"
                data-testid={`q-${q.key}-opt-${v}`}
                onClick={() => onChange(v)}
                className={`px-3 py-2 rounded-md text-sm font-medium border text-left transition-all ${
                  active
                    ? 'bg-orange-600 text-white border-orange-600 shadow-sm'
                    : 'bg-white border-slate-200 text-slate-700 hover:border-orange-300'
                }`}
              >
                {opt.label}
              </button>
            );
          })}
        </div>
      </div>
    );
  }

  if (q.type === 'multi_choice') {
    const selected = Array.isArray(value) ? value : [];
    return (
      <div className="space-y-2">
        <Label className="text-slate-700 font-medium">
          {q.label}{q.required && <span className="text-red-500"> *</span>}
        </Label>
        {q.description && <p className="text-xs text-slate-500">{q.description}</p>}
        <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
          {(q.options || []).map(opt => {
            const v = opt.value ?? opt.label;
            const active = selected.includes(v);
            return (
              <button
                key={v}
                type="button"
                onClick={() => onToggleMulti(v)}
                className={`px-3 py-2 rounded-md text-sm font-medium border text-left transition-all ${
                  active
                    ? 'bg-orange-600 text-white border-orange-600 shadow-sm'
                    : 'bg-white border-slate-200 text-slate-700 hover:border-orange-300'
                }`}
              >
                {opt.label}
              </button>
            );
          })}
        </div>
      </div>
    );
  }

  // default: text
  return (
    <div className="space-y-2">
      <Label className="text-slate-700 font-medium">
        {q.label}{q.required && <span className="text-red-500"> *</span>}
      </Label>
      {q.description && <p className="text-xs text-slate-500">{q.description}</p>}
      <Input
        data-testid={`q-${q.key}`}
        placeholder={q.placeholder || ''}
        value={value || ''}
        onChange={e => onChange(e.target.value)}
        className={baseInput}
      />
    </div>
  );
}

function TimerStep({
  config, durationMin, onDurationChange, remaining, running, ended,
  onStart, onPause, onReset, tipIndex, onCycleTip, meeting
}) {
  const tips = config?.tips || [];
  const tip = tips.length ? tips[tipIndex % tips.length] : null;

  // Progress (visueel) — wat is er al verstreken
  const totalSec = Math.max(1, durationMin * 60);
  const progressPct = Math.max(0, Math.min(100, ((totalSec - remaining) / totalSec) * 100));

  const subject = meeting?.subject;

  return (
    <div data-testid="step-timer" className="space-y-5">
      {/* Subject header */}
      {subject && (
        <div className="text-center">
          <div className="inline-flex items-center gap-2 text-xs uppercase tracking-wider text-orange-700 font-semibold mb-1">
            <Sparkles className="w-3.5 h-3.5" /> Jouw meeting
          </div>
          <h2 className="text-2xl font-bold text-slate-900">{subject}</h2>
        </div>
      )}

      {/* Timer card */}
      <Card className={`border-orange-100 shadow-lg overflow-hidden transition-all ${ended ? 'ring-2 ring-orange-500/40' : ''}`}>
        <CardContent className="p-6 md:p-8">
          {/* Duration selector (alleen als niet running) */}
          {!running && !ended && (
            <div className="mb-4">
              <Label className="text-slate-600 text-xs uppercase tracking-wider">Timer-duur (minuten)</Label>
              <div className="flex items-center gap-2 mt-2 flex-wrap">
                {[5, 10, 15, 25, 30, 45, 60, 90].map(p => (
                  <button
                    key={p}
                    type="button"
                    data-testid={`timer-preset-${p}`}
                    onClick={() => onDurationChange(p)}
                    className={`px-3 py-1.5 rounded-full text-sm font-medium border transition-all ${
                      Number(durationMin) === p
                        ? 'bg-orange-600 text-white border-orange-600'
                        : 'bg-white border-slate-200 text-slate-700 hover:border-orange-300'
                    }`}
                  >
                    {p}
                  </button>
                ))}
                <Input
                  type="number"
                  min="1"
                  max="480"
                  value={durationMin}
                  onChange={e => onDurationChange(e.target.value)}
                  className="w-24 bg-white border-slate-200 focus-visible:ring-orange-500"
                />
              </div>
            </div>
          )}

          {/* Countdown */}
          <div className="text-center my-2">
            <div className={`text-6xl md:text-8xl font-extrabold tabular-nums tracking-tight transition-colors ${
              ended ? 'text-orange-600' :
              remaining <= 10 && running ? 'text-red-500 animate-pulse' :
              'text-slate-900'
            }`}
              data-testid="timer-display"
            >
              {formatTime(remaining)}
            </div>
            <div className="text-xs uppercase tracking-widest text-slate-400 mt-2">
              {ended ? 'Tijd is om!' : running ? 'Loopt' : remaining === durationMin * 60 ? 'Klaar om te starten' : 'Gepauzeerd'}
            </div>
          </div>

          {/* Progress bar */}
          <div className="w-full bg-slate-100 rounded-full h-2 overflow-hidden my-4">
            <div
              className={`h-full transition-all duration-300 ${ended ? 'bg-orange-500' : 'bg-gradient-to-r from-orange-500 to-amber-500'}`}
              style={{ width: `${progressPct}%` }}
            />
          </div>

          {/* Controls */}
          <div className="flex flex-wrap items-center justify-center gap-3 mt-4">
            {!running ? (
              <Button
                size="lg"
                onClick={onStart}
                data-testid="timer-start-btn"
                className="bg-orange-600 hover:bg-orange-700 text-white px-8"
              >
                <Play className="w-5 h-5 mr-2" />
                {ended ? 'Opnieuw' : remaining === durationMin * 60 ? 'Start' : 'Hervat'}
              </Button>
            ) : (
              <Button
                size="lg"
                onClick={onPause}
                data-testid="timer-pause-btn"
                variant="outline"
                className="border-orange-300 text-orange-700 hover:bg-orange-50 px-8"
              >
                <Pause className="w-5 h-5 mr-2" /> Pauze
              </Button>
            )}
            <Button
              size="lg"
              variant="outline"
              onClick={onReset}
              data-testid="timer-reset-btn"
              className="border-slate-200 text-slate-700"
            >
              <RotateCcw className="w-5 h-5 mr-2" /> Reset
            </Button>
            {ended && (
              <Button
                size="lg"
                variant="outline"
                onClick={() => playEndSound(config?.sound_key || 'bell')}
                className="border-orange-300 text-orange-700 hover:bg-orange-50"
              >
                <Volume2 className="w-5 h-5 mr-2" /> Speel opnieuw af
              </Button>
            )}
          </div>

          {ended && (
            <div className="mt-5 text-center">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-orange-100 text-orange-800 text-sm font-medium">
                <Check className="w-4 h-4" /> Goed gedaan! Sluit de meeting netjes af met acties en eigenaars.
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Boek een meeting CTA — tonen zodra de meeting voorbij is */}
      {ended && (
        <BookingCta config={config} variant="orange" />
      )}

      {/* Tip card */}
      {tip && (
        <Card className="border-amber-100 bg-gradient-to-br from-amber-50 to-orange-50/40 shadow-sm">
          <CardContent className="p-5">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-full bg-amber-200/70 flex items-center justify-center flex-shrink-0">
                <Lightbulb className="w-5 h-5 text-amber-700" />
              </div>
              <div className="flex-1">
                <div className="flex items-center justify-between mb-1">
                  <p className="text-xs font-bold uppercase tracking-wider text-amber-800">Vergader-tip</p>
                  {tips.length > 1 && (
                    <button
                      onClick={onCycleTip}
                      data-testid="tip-next-btn"
                      className="text-[11px] text-amber-700 hover:text-amber-900 underline"
                    >
                      Volgende tip
                    </button>
                  )}
                </div>
                <p className="text-slate-800 leading-relaxed" data-testid="tip-text">{tip.text}</p>
                {tip.category && (
                  <span className="inline-block mt-2 text-[10px] uppercase tracking-wider text-amber-700/80 font-medium">
                    {tip.category}
                  </span>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
